import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Plus, Settings2, Trash2, RotateCcw, Pause, Play, HardDrive, HelpCircle, X, Globe } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fmtStorage } from "../lib/planStyles";
import { GlassCard, Modal, Spinner, EmptyState } from "../components/common";
import { useAuth } from "../context/AuthContext";

const HOW_STEPS = [
  "Crie um bot no @BotFather do Telegram e copie o token.",
  "Clique em '+ Novo bot' e cole o token — o menu interativo é ativado automaticamente.",
  "Em Configurações, autorize os IDs de administradores — o storage em nuvem já vem pronto (gerenciado pelo admin).",
  "Cada bot funciona como uma operação independente, com storage, acessos e acervo próprios.",
];

export default function Bots() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const navigate = useNavigate();
  const [bots, setBots] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [token, setToken] = useState("");
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [purgeStorage, setPurgeStorage] = useState(false);
  const [showHow, setShowHow] = useState(false);

  const load = () => api.get("/bots").then((r) => setBots(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const maxBots = user.max_bots ?? 1;
  const ownBots = (bots || []).filter((b) => b.owner_id === user.id).length;
  const atLimit = !isAdmin && bots !== null && ownBots >= maxBots;

  const createBot = async () => {
    if (!token.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/bots", { token: token.trim() });
      toast.success(`Bot @${data.username} conectado${data.webhook_active ? " e menu ativado" : ""}!`);
      setShowNew(false);
      setToken("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const pauseResume = async (b) => {
    const action = b.status === "paused" ? "webhook" : "pause";
    setBusy(`${b.id}:${action}`);
    try {
      await api.post(`/bots/${b.id}/${action}`);
      toast.success(action === "pause" ? `@${b.username} parado — o bot não responde mais até retomar` : `@${b.username} retomado e menu reativado`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const restart = async (b) => {
    setBusy(`${b.id}:restart`);
    try {
      await api.post(`/bots/${b.id}/restart`);
      toast.success(`@${b.username} reiniciado — instância reconectada do zero`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const toggleGlobal = async (b) => {
    setBusy(`${b.id}:global`);
    try {
      const { data } = await api.post(`/bots/${b.id}/toggle-global`);
      toast.success(data.is_global ? `@${b.username} agora é GLOBAL — clientes só enviam, só o dono apaga` : `@${b.username} voltou a ser privado`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const removeBot = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/bots/${confirmDelete.id}${purgeStorage ? "?purge_storage=true" : ""}`);
      toast.success(purgeStorage
        ? `Bot @${confirmDelete.username} removido — ${data.purged_files || 0} ficheiro(s) apagados do armazenamento`
        : `Bot @${confirmDelete.username} removido`);
      setConfirmDelete(null);
      setPurgeStorage(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="bots-page">
      <GlassCard className="p-7 sm:p-9 anim-fade-up border-cyan-400/15" data-testid="instances-header">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
          <div>
            <p className="text-xs font-mono2 uppercase tracking-[0.3em] text-cyan-400/80 mb-2 flex items-center gap-2">
              <Bot size={13} /> Central de Instâncias
            </p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">Media Bots Telegram</h1>
            <p className="text-sm text-slate-400 mt-2 max-w-lg">Cada bot funciona como uma operação independente, com storage, acessos e acervo próprios.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="como-funciona-btn" onClick={() => setShowHow(!showHow)} className="btn-ghost-dark px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-300 inline-flex items-center gap-2">
              <HelpCircle size={14} /> Como funciona
            </button>
            <div className="glass rounded-xl px-4 py-2.5 text-center" data-testid="central-storage-card">
              <p className="font-display text-lg font-bold text-white leading-none flex items-center gap-1.5"><HardDrive size={14} className="text-cyan-400" /> {fmtStorage(Math.round(((user.storage_used_bytes || 0) / 1e9) * 100) / 100)}</p>
              <p className="text-[9px] font-mono2 uppercase tracking-widest text-slate-500 mt-1">Storage</p>
            </div>
            <div className="glass rounded-xl px-4 py-2.5 text-center" data-testid="central-bots-count">
              <p className="font-display text-lg font-bold text-white leading-none">{isAdmin ? (bots?.length ?? "—") : `${ownBots}/${maxBots}`}</p>
              <p className="text-[9px] font-mono2 uppercase tracking-widest text-slate-500 mt-1">Bots</p>
            </div>
            <button
              data-testid="novo-bot-btn"
              onClick={() => (atLimit ? toast.error(`Limite da sua conta: ${maxBots} bot(s). Para criar mais, fale com o suporte.`) : setShowNew(true))}
              title={atLimit ? `Limite da sua conta: ${maxBots} bot(s)` : ""}
              className={`btn-neon px-5 py-3 rounded-xl text-sm inline-flex items-center gap-2 ${atLimit ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Plus size={16} /> Novo bot
            </button>
          </div>
        </div>
        {showHow && (
          <div className="mt-6 p-5 rounded-xl bg-slate-950/50 border border-slate-800/60 anim-fade-up" data-testid="how-it-works-panel">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display text-sm font-semibold text-white">Como funciona</h4>
              <button onClick={() => setShowHow(false)} className="text-slate-500 hover:text-white" data-testid="how-close-btn"><X size={15} /></button>
            </div>
            <ol className="space-y-2">
              {HOW_STEPS.map((s, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-300">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-cyan-400/15 border border-cyan-400/30 text-cyan-300 text-[10px] font-mono2 flex items-center justify-center">{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </GlassCard>

      <div className="anim-fade-up anim-delay-1">
        <p className="text-[11px] font-mono2 uppercase tracking-[0.25em] text-slate-500 mb-1">Bots configurados</p>
        <p className="text-xs text-slate-600 mb-4">As instâncias ficam minimizadas. Abra somente a que deseja administrar.</p>

        {bots === null ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : bots.length === 0 ? (
          <EmptyState icon={Bot} title="Nenhum bot configurado" subtitle="Clique em '+ Novo bot' e cole o token do @BotFather." testid="bots-empty" />
        ) : (
          <div className="space-y-3" data-testid="bots-list">
            {bots.map((b) => {
              const paused = b.status === "paused";
              return (
                <GlassCard key={b.id} className="overflow-hidden" data-testid={`bot-row-${b.id}`}>
                  <div className="p-4 sm:p-5 flex flex-wrap items-center gap-3 cursor-pointer" onClick={() => navigate(`/app/bots/${b.id}`)}>
                    <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${paused ? "bg-amber-400/10 border-amber-400/25" : "bg-emerald-400/10 border-emerald-400/25"}`}>
                      <Bot size={18} className={paused ? "text-amber-400" : "text-emerald-400"} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white font-mono2 flex items-center gap-2">@{b.username}
                        {b.is_global && <span className="text-[9px] font-mono2 uppercase tracking-widest text-cyan-300 bg-cyan-400/10 border border-cyan-400/25 rounded-full px-2 py-0.5" data-testid={`bot-global-badge-${b.id}`}>Global</span>}
                      </p>
                      <p className={`text-[11px] flex items-center gap-1.5 mt-0.5 ${paused ? "text-amber-300" : b.online ? "text-emerald-300" : "text-rose-400"}`} data-testid={`bot-status-${b.id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${paused ? "bg-amber-400" : b.online ? "bg-emerald-400" : "bg-rose-400"}`} />
                        {paused ? "Parado" : b.online ? "Ativo" : "Offline"}
                        {!paused && b.webhook_active && <span className="text-slate-500">· menu ativo</span>}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 ml-auto" onClick={(e) => e.stopPropagation()}>
                      {isAdmin && (
                        <button
                          data-testid={`bot-global-btn-${b.id}`}
                          onClick={() => toggleGlobal(b)}
                          disabled={busy === `${b.id}:global`}
                          title={b.is_global ? "Remover modo global" : "Tornar global — clientes só enviam conteúdo, só o dono apaga"}
                          className={`px-3.5 py-2.5 rounded-xl text-xs inline-flex items-center gap-1.5 ${b.is_global ? "bg-cyan-400/15 border border-cyan-400/40 text-cyan-300" : "btn-ghost-dark text-slate-300"}`}
                        >
                          {busy === `${b.id}:global` ? <Spinner /> : <Globe size={13} />} {b.is_global ? "Global ✓" : "Tornar global"}
                        </button>
                      )}
                      {(isAdmin || !b.is_global || b.owner_id === user.id) && (<>
                      <button
                        data-testid={`bot-pause-btn-${b.id}`}
                        onClick={() => pauseResume(b)}
                        disabled={busy === `${b.id}:pause` || busy === `${b.id}:webhook`}
                        title={paused ? "Retomar o bot" : "Parar o bot (para de responder no Telegram)"}
                        className="btn-ghost-dark px-3.5 py-2.5 rounded-xl text-xs text-slate-300 inline-flex items-center gap-1.5"
                      >
                        {busy === `${b.id}:pause` || busy === `${b.id}:webhook` ? <Spinner /> : paused ? <Play size={13} /> : <Pause size={13} />}
                        {paused ? "Retomar" : "Parar"}
                      </button>
                      <button
                        data-testid={`bot-restart-btn-${b.id}`}
                        onClick={() => restart(b)}
                        disabled={busy === `${b.id}:restart`}
                        title="Reiniciar a instância — reconecta o bot do zero"
                        className="btn-ghost-dark px-3.5 py-2.5 rounded-xl text-xs text-slate-300 inline-flex items-center gap-1.5"
                      >
                        {busy === `${b.id}:restart` ? <Spinner /> : <RotateCcw size={13} />} Reiniciar
                      </button>
                      {!b.is_default && (
                        <button
                          data-testid={`bot-delete-btn-${b.id}`}
                          onClick={() => setConfirmDelete(b)}
                          title="Excluir instância"
                          className="btn-ghost-dark px-3.5 py-2.5 rounded-xl text-xs text-rose-300 inline-flex items-center gap-1.5"
                        >
                          <Trash2 size={13} /> Excluir
                        </button>
                      )}
                      </>)}
                      <button
                        data-testid={`bot-config-btn-${b.id}`}
                        onClick={() => navigate(`/app/bots/${b.id}`)}
                        className="btn-violet px-4 py-2.5 rounded-xl text-xs inline-flex items-center gap-2"
                      >
                        <Settings2 size={14} /> Configurações
                      </button>
                    </div>
                  </div>
                </GlassCard>
              );
            })}
          </div>
        )}
      </div>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="Conectar novo bot" testid="new-bot-modal">
        <div className="space-y-4">
          <p className="text-sm text-slate-400">Crie um bot com o <span className="text-cyan-300 font-mono2">@BotFather</span> no Telegram e cole o token abaixo. O menu interativo é ativado automaticamente.</p>
          <input
            data-testid="new-bot-token-input"
            className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2"
            placeholder="123456789:AAE..."
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
          <button data-testid="new-bot-submit" onClick={createBot} disabled={creating || !token.trim()} className="btn-neon w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
            {creating ? <Spinner /> : <><Plus size={15} /> Conectar bot</>}
          </button>
        </div>
      </Modal>

      <Modal open={!!confirmDelete} onClose={() => { setConfirmDelete(null); setPurgeStorage(false); }} title="Excluir instância" testid="delete-bot-modal">
        <div className="space-y-4">
          <p className="text-sm text-slate-400">
            O bot <span className="text-white font-mono2">@{confirmDelete?.username}</span> será desconectado e removido da plataforma. Os links M3U dele param de funcionar de imediato.
          </p>
          <label className="flex items-start gap-3 rounded-xl border border-rose-400/25 bg-rose-400/10 px-4 py-3 cursor-pointer" data-testid="purge-storage-option">
            <input
              type="checkbox"
              data-testid="purge-storage-checkbox"
              checked={purgeStorage}
              onChange={(e) => setPurgeStorage(e.target.checked)}
              className="mt-0.5 accent-rose-500"
            />
            <span className="text-xs text-rose-200 leading-relaxed">
              <b>Apagar também os ficheiros no armazenamento em nuvem</b> — todo o acervo deste bot é removido do storage e o espaço é libertado da sua cota. <b>Irreversível.</b>
            </span>
          </label>
          {!purgeStorage && (
            <p className="text-[11px] text-slate-500" data-testid="purge-storage-hint">Sem marcar a opção acima, os ficheiros ficam guardados e o espaço continua a contar na sua cota.</p>
          )}
          <div className="flex gap-3 justify-end">
            <button data-testid="delete-bot-cancel" onClick={() => { setConfirmDelete(null); setPurgeStorage(false); }} className="btn-ghost-dark px-5 py-2.5 rounded-xl text-xs text-slate-300">Cancelar</button>
            <button data-testid="delete-bot-confirm" onClick={removeBot} disabled={deleting} className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-rose-500/90 hover:bg-rose-500 text-white inline-flex items-center gap-2">
              {deleting ? <Spinner /> : <Trash2 size={13} />} {purgeStorage ? "Excluir bot e ficheiros" : "Excluir bot"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
